import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { SelectedVehicleContext } from '../../../contexts/vehicle/SelectedVehicleContext.ts';
import { Place } from '../../../schema/Place.ts';
import { RoadmapType } from '../../../schema/Roadmap.ts';
import { Territory } from '../../../schema/Territory.ts';
import { useTerritoryConfig } from '../../../services/Territory.ts';
import { getCurrentPlace, getNextPlace, refreshSelectedPlace } from '../../../utils/roadmapUtils.ts';
import { CustomSpinner } from '../../ui/Spinner.tsx';
import { RoadmapActions } from './RoadmapActions.tsx';
import { RoadmapStepper } from './RoadmapStepper.tsx';
import { ShiftActions } from './ShiftActions.tsx';

interface RoadmapProps {
  territory: Territory | undefined;
  roadmapData: RoadmapType | null | undefined;
  dataLoading: boolean;
}

export function Roadmap({ territory, roadmapData, dataLoading }: RoadmapProps) {
  const params = useParams() as { territorySlug: string };
  const { selectedVehicle } = useContext(SelectedVehicleContext);
  const ref = useRef<null | HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [selectedPlace, setSelectedPlace] = useState<null | Place>(null);
  const [showPlaceDetails, setShowPlaceDetails] = useState(false);
  const { data: territoryConfig } = useTerritoryConfig(params.territorySlug, selectedVehicle?.id);

  const handleClosePlaceDetails = () => setShowPlaceDetails(false);
  function handleShowPlaceDetails(place: Place) {
    setSelectedPlace(place);
    setShowPlaceDetails(true);
  }

  let currentPlace;
  let nextPlace;
  let refreshedSelectedPlace;

  if (roadmapData) {
    currentPlace = getCurrentPlace(roadmapData.places);
    nextPlace = getNextPlace(roadmapData.places);
    refreshedSelectedPlace = refreshSelectedPlace(selectedPlace, roadmapData.places);
  } else {
    currentPlace = undefined;
    nextPlace = undefined;
    refreshedSelectedPlace = selectedPlace;
  }

  const scrollTargetId = nextPlace?.id ?? currentPlace?.id;

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      ref.current?.scrollIntoView({
        behavior: 'instant',
        inline: 'center',
        block: 'nearest',
      });
    });
  }, [scrollTargetId]);

  useEffect(() => {
    setSelectedPlace(refreshedSelectedPlace);
  }, [refreshedSelectedPlace]);
  const hasPlaces = roadmapData?.places && roadmapData.places.length > 0;
  const showNoRoadmap = !dataLoading && !hasPlaces;
  const showStepper = !dataLoading && hasPlaces && currentPlace && nextPlace;

  return (
    <>
      {territory?.permissions.can_perform_pause_shift && territoryConfig?.shift?.can_be_paused && (
        <ShiftActions />
      )}

      <div className="col-10 ps-0 overflow-x-auto my-0 d-flex align-items-center justify-content-start">
        {dataLoading && <CustomSpinner />}

        {showNoRoadmap && <div className="text-basic ms-4">{t('roadmap.no-roadmap')}</div>}

        {showStepper && currentPlace && nextPlace && (
          <RoadmapStepper
            places={roadmapData!.places}
            currentPlace={currentPlace}
            nextPlace={nextPlace}
            onPlaceClick={handleShowPlaceDetails}
            scrollRef={ref}
          />
        )}

        <RoadmapActions
          handleClosePlaceDetails={handleClosePlaceDetails}
          showPlaceDetails={showPlaceDetails}
          currentPlace={currentPlace}
          selectedPlace={selectedPlace}
          nextPlace={nextPlace}
        />
      </div>
    </>
  );
}
